
import React from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { useNavigate } from 'react-router-dom';
import { Gift, Plus, Package, BarChart3 } from 'lucide-react';
import { useRequireAuth } from '@/hooks/useRequireAuth';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import GiftList from '@/components/gift/GiftList';
import GiftInventoryList from '@/components/gift/GiftInventoryList';
import { GoogleSurveyIntegration } from '@/components/gift/GoogleSurveyIntegration';

const Gifts = () => {
  useRequireAuth();
  const navigate = useNavigate();

  const { data: gifts, isLoading: isLoadingGifts } = useQuery({
    queryKey: ['gifts'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('gifts')
        .select(`
          id, 
          name, 
          date_sent, 
          status, 
          value, 
          shipping_status,
          contact_id,
          contacts(first_name, last_name),
          organization_id,
          organizations(name)
        `)
        .order('date_sent', { ascending: false });
        
      if (error) {
        throw new Error(error.message);
      }
      
      return data || [];
    },
  });

  const { data: inventory, isLoading: isLoadingInventory } = useQuery({
    queryKey: ['gift_inventory'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('gift_inventory')
        .select('*')
        .order('gift_name', { ascending: true });
        
      if (error) {
        throw new Error(error.message);
      }
      
      return data || [];
    },
  });

  return (
    <div className="space-y-6 p-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Gift Management</h1>
          <p className="text-muted-foreground">
            Manage client gifts and gift inventory
          </p>
        </div>
        <div className="flex gap-3">
          <Button onClick={() => navigate('/gifts/inventory/create')}>
            <Package className="mr-2 h-4 w-4" />
            Add Inventory
          </Button>
          <Button onClick={() => navigate('/gifts/create')}>
            <Plus className="mr-2 h-4 w-4" />
            New Gift
          </Button>
        </div>
      </div>

      <Tabs defaultValue="gifts">
        <TabsList>
          <TabsTrigger value="gifts">
            <Gift className="mr-2 h-4 w-4" />
            Client Gifts
          </TabsTrigger>
          <TabsTrigger value="inventory">
            <Package className="mr-2 h-4 w-4" />
            Gift Inventory
          </TabsTrigger>
          <TabsTrigger value="feedback">
            <BarChart3 className="mr-2 h-4 w-4" />
            Feedback Survey
          </TabsTrigger>
        </TabsList>

        <TabsContent value="gifts" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Gift History</CardTitle>
            </CardHeader>
            <CardContent>
              <GiftList gifts={gifts || []} isLoading={isLoadingGifts} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="inventory" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Gift Inventory</CardTitle>
            </CardHeader>
            <CardContent>
              <GiftInventoryList inventory={inventory || []} isLoading={isLoadingInventory} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="feedback" className="mt-6">
          <GoogleSurveyIntegration />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Gifts;
